import api, { getLinks } from '../api';

import {
  importFetchedAccounts,
  importFetchedStatuses,
  importFetchedStatus,
} from './importer';

import { generateConversationKey, distributeConversationKey } from 'mastodon/e2ee/keys';
import { openModal } from 'mastodon/actions/modal';

/**
 * Toggle E2EE on a conversation. If enabling, generate a conversation key and
 * distribute it (sealed) to all participants by POSTing to the /keys endpoint.
 */
export const toggleConversationE2EE = (conversationId, enable) => async (dispatch, getState) => {
  try {
    // If enabling E2EE, ensure the current user has an account E2EE key. If not,
    // prompt them to generate/upload a key first via a modal.
    if (enable) {
      const me = getState().getIn(['meta', 'me']);
      const myAccount = getState().getIn(['accounts', me]);
      if (!myAccount || !myAccount.e2ee_public_key) {
        // Open a modal that offers to generate & upload a key, and then proceed
        dispatch(openModal({ modalType: 'CONFIRM_ENABLE_E2EE', modalProps: { conversationId } }));
        return;
      }
    }

    const response = await api().patch(`/api/v1/conversations/${conversationId}`, { e2ee: enable });
    dispatch(updateConversations(response.data));

    if (enable) {
      const accounts = response.data.accounts || [];
      // Ensure everyone has an e2ee public key
      const missing = accounts.filter(a => !a.e2ee_public_key);
      if (missing.length > 0) {
        dispatch(openModal({ modalType: 'ALERT', modalProps: { title: 'E2EE key missing', body: 'One or more participants do not have an E2EE public key. Ask them to generate/upload an account E2EE key before enabling E2EE for this conversation.' } }));
        // Revert the enable on the server to avoid partial state
        await api().patch(`/api/v1/conversations/${conversationId}`, { e2ee: false });
        dispatch(updateConversations({ id: conversationId, e2ee: false }));
        return;
      }

      const participants = accounts.map(a => ({ accountId: a.id, publicKeyBase64: a.e2ee_public_key }));
      const convKey = generateConversationKey();

      try {
        await distributeConversationKey(conversationId, convKey, participants);
        dispatch(openModal({ modalType: 'ALERT', modalProps: { title: 'E2EE enabled', body: 'Conversation key distributed to participants.' } }));
      } catch (e) {
        // On failure, revert the conversation e2ee flag
        await api().patch(`/api/v1/conversations/${conversationId}`, { e2ee: false });
        dispatch(updateConversations({ id: conversationId, e2ee: false }));
        dispatch(openModal({ modalType: 'ALERT', modalProps: { title: 'E2EE failed', body: 'Failed to distribute conversation key to participants: ' + (e.message || e) } }));
      }
    }
  } catch (err) {
    dispatch(openModal({ modalType: 'ALERT', modalProps: { title: 'E2EE error', body: 'Failed to update conversation settings: ' + (err.message || err) } }));
  }
};

/**
 * Enable E2EE for a conversation assuming the current user now has an account key.
 * This is used after a user generates and uploads a key via the enable modal.
 * Note: Key distribution to participants will be implemented when we add message encryption.
 */
export const enableConversationE2EEWithConvKey = (conversationId) => async (dispatch) => {
  try {
    const response = await api().patch(`/api/v1/conversations/${conversationId}`, { e2ee: true });
    dispatch(updateConversations(response.data));
    // Success - E2EE flag is now enabled on the conversation
  } catch (err) {
    dispatch(openModal({ modalType: 'ALERT', modalProps: { title: 'E2EE error', body: 'Failed to enable E2EE: ' + (err.message || err) } }));
    throw err; // Re-throw so the modal can handle it
  }
};

export const CONVERSATIONS_MOUNT   = 'CONVERSATIONS_MOUNT';
export const CONVERSATIONS_UNMOUNT = 'CONVERSATIONS_UNMOUNT';

export const CONVERSATIONS_FETCH_REQUEST = 'CONVERSATIONS_FETCH_REQUEST';
export const CONVERSATIONS_FETCH_SUCCESS = 'CONVERSATIONS_FETCH_SUCCESS';
export const CONVERSATIONS_FETCH_FAIL    = 'CONVERSATIONS_FETCH_FAIL';
export const CONVERSATIONS_UPDATE        = 'CONVERSATIONS_UPDATE';

export const CONVERSATIONS_READ = 'CONVERSATIONS_READ';

export const CONVERSATIONS_DELETE_REQUEST = 'CONVERSATIONS_DELETE_REQUEST';
export const CONVERSATIONS_DELETE_SUCCESS = 'CONVERSATIONS_DELETE_SUCCESS';
export const CONVERSATIONS_DELETE_FAIL    = 'CONVERSATIONS_DELETE_FAIL';

export const mountConversations = () => ({
  type: CONVERSATIONS_MOUNT,
});

export const unmountConversations = () => ({
  type: CONVERSATIONS_UNMOUNT,
});

export const markConversationRead = conversationId => (dispatch) => {
  dispatch({
    type: CONVERSATIONS_READ,
    id: conversationId,
  });

  api().post(`/api/v1/conversations/${conversationId}/read`);
};

export const expandConversations = ({ maxId } = {}) => (dispatch, getState) => {
  dispatch(expandConversationsRequest());

  const params = { max_id: maxId };

  if (!maxId) {
    params.since_id = getState().getIn(['conversations', 'items', 0, 'last_status']);
  }

  const isLoadingRecent = !!params.since_id;

  api().get('/api/v1/conversations', { params })
    .then(response => {
      const next = getLinks(response).refs.find(link => link.rel === 'next');

      dispatch(importFetchedAccounts(response.data.reduce((aggr, item) => aggr.concat(item.accounts), [])));
      dispatch(importFetchedStatuses(response.data.map(item => item.last_status).filter(x => !!x)));
      dispatch(expandConversationsSuccess(response.data, next ? next.uri : null, isLoadingRecent));
    })
    .catch(err => dispatch(expandConversationsFail(err)));
};

export const expandConversationsRequest = () => ({
  type: CONVERSATIONS_FETCH_REQUEST,
});

export const expandConversationsSuccess = (conversations, next, isLoadingRecent) => ({
  type: CONVERSATIONS_FETCH_SUCCESS,
  conversations,
  next,
  isLoadingRecent,
});

export const expandConversationsFail = error => ({
  type: CONVERSATIONS_FETCH_FAIL,
  error,
});

export const updateConversations = conversation => dispatch => {
  dispatch(importFetchedAccounts(conversation.accounts));

  if (conversation.last_status) {
    dispatch(importFetchedStatus(conversation.last_status));
  }

  dispatch({
    type: CONVERSATIONS_UPDATE,
    conversation,
  });
};

export const deleteConversation = conversationId => (dispatch) => {
  dispatch(deleteConversationRequest(conversationId));

  api().delete(`/api/v1/conversations/${conversationId}`)
    .then(() => dispatch(deleteConversationSuccess(conversationId)))
    .catch(error => dispatch(deleteConversationFail(conversationId, error)));
};

export const deleteConversationRequest = id => ({
  type: CONVERSATIONS_DELETE_REQUEST,
  id,
});

export const deleteConversationSuccess = id => ({
  type: CONVERSATIONS_DELETE_SUCCESS,
  id,
});

export const deleteConversationFail = (id, error) => ({
  type: CONVERSATIONS_DELETE_FAIL,
  id,
  error,
});
